import { isAxiosError } from "axios";
import { format } from "date-fns";
import {
  Button,
  DropDownButton,
  LoadPanel,
  Pagination,
  ScrollView,
} from "devextreme-react";
import { confirm } from "devextreme/ui/dialog";
import notify from "../../lib/notify";
import i18n from "i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import AnnouncementCreatePopup from "../../components/announcement-create-popup/AnnouncementCreatePopup";
import AnnouncementEditPopup from "../../components/announcement-edit-popup/AnnouncementEditPopup";
import { useAuth } from "../../contexts/auth";
import axios from "../../lib/axios";
import { Announcement, Paginated, RoleEnum } from "../../types";

const Announcements = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [paginatedAnnouncements, setPaginatedAnnouncements] =
    useState<Paginated<Announcement>>();
  const [showAnnouncementCreatePopup, setShowAnnouncementCreatePopup] =
    useState(false);
  const [announcementToEdit, setAnnouncementToEdit] = useState<Announcement>();

  const role = user?.roles;
  const isNotEmployee = role && !role.includes(RoleEnum.Pracownik);
  const isNotMajster = role && !role.includes(RoleEnum.Majster);

  const showManageDropdown = useMemo(
    () =>
      user?.roles?.includes(RoleEnum.Kierownik) ||
      user?.roles?.includes(RoleEnum.KadrySpolki) ||
      user?.roles?.includes(RoleEnum.AdministratorSpolki) ||
      user?.roles?.includes(RoleEnum.AdministratorGlowny) ||
      user?.roles?.includes(RoleEnum.Manager),
    [user],
  );

  const canManageAnnouncement = (announcement: Announcement) => {
    const isCreator = user?.employeeId === announcement.employeeId;
    const isAdminOrManager =
      user?.roles?.includes(RoleEnum.AdministratorGlowny) ||
      user?.roles?.includes(RoleEnum.AdministratorSpolki) ||
      user?.roles?.includes(RoleEnum.Manager);

    return isCreator || isAdminOrManager;
  };

  const fetchAnnouncements = useCallback(() => {
    setLoading(true);
    axios
      .get<Paginated<Announcement>>("/api/announcements", {
        params: {
          pageNumber,
        },
        headers: {
          "Accept-Language": i18n.language,
        },
      })
      .then(({ data }) => setPaginatedAnnouncements(data))
      .catch((error) => {
        let message = t("common.unknownErrorOccurred");

        if (isAxiosError(error)) {
          if (error.response) {
            if (error.response.status === 403) {
              message = t("noPermissions");
            } else if (
              error.response.data &&
              "message" in error.response.data
            ) {
              message = error.response.data.message;
            }
          } else if (error.request) {
            message = t("common.noResponseFromServer");
          }
        }

        notify(message, "error", 3000);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pageNumber, t]);

  const sendAnnouncementDeleteRequest = useCallback(
    (id: number) => {
      setLoading(true);
      axios
        .delete(`/api/announcements/${id}`, {
          headers: {
            "Accept-Language": i18n.language,
          },
        })
        .then(() => {
          notify(t("announcements.delete.messages.deleted"), "success");
          fetchAnnouncements();
        })
        .catch((error) => {
          let message = t("common.unknownErrorOccurred");

          if (isAxiosError(error)) {
            if (error.response) {
              if (error.response.status === 403) {
                message = t("noPermissions");
              } else if (
                error.response.data &&
                "message" in error.response.data
              ) {
                message = error.response.data.message;
              }
            } else if (error.request) {
              message = t("common.noResponseFromServer");
            }
          }

          notify(message, "error", 5000);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [fetchAnnouncements, t],
  );

  useEffect(
    function () {
      fetchAnnouncements();
    },
    [fetchAnnouncements, pageNumber],
  );

  if (loading) {
    return <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />;
  }

  return (
    <ScrollView useNative>
      <div className="p-6">
        {isNotEmployee && showAnnouncementCreatePopup && isNotMajster && (
          <AnnouncementCreatePopup
            onClose={(announcementCreated: boolean) => {
              setShowAnnouncementCreatePopup(false);

              if (announcementCreated) {
                notify(t("announcements.create.messages.created"), "success");
                fetchAnnouncements();
              }
            }}
          />
        )}
        {isNotEmployee && isNotMajster && announcementToEdit && (
          <AnnouncementEditPopup
            id={announcementToEdit.id}
            title={announcementToEdit.title}
            content={announcementToEdit.content}
            roleIds={announcementToEdit.roles.map(({ id }) => id)}
            projectIds={announcementToEdit.projects.map(
              ({ projectId }) => projectId,
            )}
            files={announcementToEdit.files?.map((f) => ({
              id: f.id,
              fileName: f.fileName,
              url: f.url,
            }))}
            onClose={(announcementUpdated) => {
              setAnnouncementToEdit(undefined);

              if (announcementUpdated) {
                notify(t("announcements.edit.messages.updated"), "success");
                fetchAnnouncements();
              }
            }}
          />
        )}
        <div className="mb-6 flex flex-col md:flex-row gap-2 justify-between items-start">
          <div>
            <div className="mb-2 text-2xl font-bold leading-none">
              {t("announcements.list.header")}
            </div>
            <div className="text-base">{t("announcements.headerText")}</div>
          </div>
          {isNotEmployee && isNotMajster && (
            <Button
              stylingMode="contained"
              text={t("announcements.list.actions.create")}
              onClick={() => setShowAnnouncementCreatePopup(true)}
            />
          )}
        </div>
        <ScrollView useNative>
          <div className="mx-auto max-w-5xl w-full flex flex-col gap-4">
            {paginatedAnnouncements ? (
              paginatedAnnouncements.items.length === 0 ? (
                <div
                  className="p-4 mb-4 text-sm text-blue-800 rounded-lg bg-blue-50 dark:bg-gray-800 dark:text-blue-400"
                  role="alert"
                >
                  {t("announcements.list.messages.empty")}
                </div>
              ) : (
                paginatedAnnouncements.items.map((announcement, index) => (
                  <div
                    key={index}
                    className="p-4 border border-solid border-[#E0E0E0] dark:border-[#616161] rounded-md bg-white dark:bg-[#292929]"
                  >
                    <div className="mb-1 flex justify-between items-center">
                      <div className="text-lg font-bold">
                        {announcement.title}
                      </div>
                      {isNotEmployee &&
                        showManageDropdown &&
                        canManageAnnouncement(announcement) && (
                          <DropDownButton
                            keyExpr="action"
                            displayExpr="name"
                            items={[
                              {
                                action: "edit",
                                name: t("announcements.list.actions.edit"),
                                icon: "edit",
                              },
                              {
                                action: "delete",
                                name: t("announcements.list.actions.delete"),
                                icon: "trash",
                              },
                            ]}
                            onItemClick={(event) => {
                              if (event.itemData.action === "edit") {
                                setAnnouncementToEdit(announcement);
                              }

                              if (event.itemData.action === "delete") {
                                confirm(
                                  t(
                                    "announcements.delete.messages.confirmation",
                                  ),
                                  t("confirmationRequired"),
                                ).then((result) => {
                                  if (result === true) {
                                    sendAnnouncementDeleteRequest(
                                      announcement.id,
                                    );
                                  }
                                });
                              }
                            }}
                            icon="overflow"
                            showArrowIcon={false}
                            stylingMode="text"
                            dropDownOptions={{
                              width: "auto",
                            }}
                          />
                        )}
                    </div>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: announcement.content,
                      }}
                    />
                    {announcement.files?.length > 0 && (
                      <div className="mt-2 flex flex-col gap-2">
                        {announcement.files.map((file) => (
                          <div key={file.id}>
                            {file.fileName.match(
                              /\.(jpg|jpeg|png|gif|webp)$/i,
                            ) ? (
                              <img
                                src={file.url}
                                alt={file.fileName}
                                className="max-w-full rounded-md border border-gray-300"
                              />
                            ) : (
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 underline"
                              >
                                {file.fileName}
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-1">
                      <div className="text-xs">
                        {t("announcements.list.messages.createdBy", {
                          user: announcement.createdBy,
                          date: format(
                            new Date(announcement.dateCreated),
                            "dd.MM.yyyy HH:mm",
                          ),
                        })}
                      </div>
                      {announcement.updatedBy !== null &&
                      announcement.dateModified !== null ? (
                        <div className="text-xs">
                          {t("announcements.list.messages.updatedBy", {
                            user: announcement.updatedBy,
                            date: format(
                              new Date(announcement.dateModified),
                              "dd.MM.yyyy HH:mm",
                            ),
                          })}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              )
            ) : null}
            <Pagination
              showInfo
              showNavigationButtons
              showPageSizeSelector={false}
              defaultPageSize={5}
              pageIndex={pageNumber}
              onPageIndexChange={setPageNumber}
              itemCount={paginatedAnnouncements?.totalItems}
            />
          </div>
        </ScrollView>
      </div>
    </ScrollView>
  );
};

export default Announcements;
